import React, { Component, Fragment } from 'react';
import DropzoneComponent from 'react-dropzone-component';
import injectSheet from 'react-jss';

import { getCurrentUser } from '~/api/currentUser';
import { EM_DASH } from '~/common/constants/unicode';
import FieldValuesMultiSelect from '~/components/Shared/FieldValuesMultiSelect/FieldValuesMultiSelect';
import FieldValuesSingleSelect from '~/components/Shared/FieldValuesSingleSelect/FieldValuesSingleSelect';
import { Button } from '~/eds/src/components/Button/Button';
import { DateInput } from '~/eds/src/components/DateInput/DateInput';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { parseDate } from '~/eds/src/utils/parseDate';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { injectIntl } from 'react-intl';
import { withFormatDate } from '~/i18n/hocs/withFormatDate';
import { getFields } from '~/redux/api/methods/fields/getFields';
import { filterValidFiles } from '~/utils/helper.utils';

import { black5, evisortBlue } from '../../../assets/shared-styles/general';
import { MODAL_FOLDER_TREE } from '../../../types/modal.types';
import {
  booleanOptions,
  preprocessEditData,
} from '../../DocumentsViewPage/DocumentOverviewPanel/helpers';
import EcDatepicker from '../../Shared/EcDatepicker/EcDatepicker';
import EcInput from '../../Shared/EcInput/EcInput';
import EcModalCard from '../../Shared/EcModalCard/EcModalCard';
import EcMultipleSelectValue from '../../Shared/EcMultipleSelectValue/EcMultipleSelectValue';
import EcSelect from '../../Shared/EcSelect/EcSelect';
import ChevronRightIcon from '../../Shared/Icons/ChevronRightIcon';
import CloseIcon from '../../Shared/Icons/CloseIcon';
import DocumentStrokedIcon from '../../Shared/Icons/DocumentStrokedIcon';
import FolderIcon from '../../Shared/Icons/FolderIcon';
import LoadingSpinner from '../../Shared/Icons/LoadingSpinner';
import PlusIcon from '../../Shared/Icons/PlusIcon';
import SubArrowIcon from '../../Shared/Icons/SubArrowIcon';
import UploadSize from '../../UploadPage/UploadSize/UploadSize';
import styles from './UploadModal.styles';

class UploadModal extends Component {
  constructor(props) {
    super(props);

    this.handleOnUploadDocumentsClick =
      this.handleOnUploadDocumentsClick.bind(this);
    this.handleChangeFormField = this.handleChangeFormField.bind(this);
    this.state = { formData: {} };
  }
  componentDidMount() {
    this.setState({ loading: true });
    getCurrentUser()
      .then((res) => {
        this.setState({
          errorLoading: false,
          uploadFormFields: res.client_config.upload_form_fields,
        });

        const selectedFieldsIds = res.client_config.upload_form_fields.map(
          (item) => item.id,
        );

        getFields({ ids: selectedFieldsIds }).then((response) => {
          const helpText = response.results.reduce((acc, item) => {
            acc[item.id] = item.helpText;
            return acc;
          }, {});

          this.setState({ helpText });
        });
      })
      .catch((error) => {
        if (error && error.response && error.response.status !== 401) {
          this.setState({ errorLoading: true });
        }
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  }
  handleOnUploadDocumentsClick() {
    const { formData } = this.state;
    const processedFields = preprocessEditData(formData).filter(
      (field) => field.value && field.value.length,
    );
    this.props.handleUploadFiles(processedFields);
    this.props.hideModal();
  }

  handleChangeFormField(id, value) {
    const data = { [id]: value };
    this.setState({ formData: { ...this.state.formData, ...data } });
  }

  onCalendarOpenClose = (open) => {
    this.props.disableEscapeKey(open);
  };

  renderSelectedFolderPath() {
    const { classes, uploadLocationPath = [] } = this.props;
    let path = [];

    if (!Array.isArray(uploadLocationPath)) {
      path = uploadLocationPath.split('/').map((folder, index) => ({
        id: index,
        name: folder,
      }));
    } else {
      path = uploadLocationPath;
    }
    return path.length ? (
      <div className={classes.folderPath}>
        {path
          .map((pathChunk) => <span key={pathChunk.id}>{pathChunk.name}</span>)
          .reduce((prev, curr) => [
            prev,
            <ChevronRightIcon size="16" key={curr} />,
            curr,
          ])}
      </div>
    ) : null;
  }

  renderBooleanItem(item) {
    const { classes } = this.props;

    return (
      <div className={classes.fieldContainer}>
        <EcSelect
          options={booleanOptions}
          onChange={(newValue) => {
            const changeValue = newValue ? newValue.value : null;
            this.handleChangeFormField(item.id, changeValue);
          }}
          isClearable={true}
          isSearchable={false}
        />
      </div>
    );
  }

  renderDateItem(item) {
    const { classes, flags, intl, formatDate } = this.props;
    const dateField = this.state.formData[item.id];
    const enableLocalization = flags[FlagType.LanguageSelect];

    return (
      <>
        {enableLocalization ? (
          <Layout>
            <DateInput
              value={dateField ? parseDate(dateField) : null}
              width="224px"
              onChange={(date) => {
                this.setState({
                  formData: {
                    ...this.state.formData,
                    [item.id]: date ? formatDate(date, 'iso') : null,
                  },
                });
              }}
            />
            <Button
              level="tertiary"
              text={intl.formatMessage({
                id: 'UploadModal.clearDate',
                defaultMessage: 'Clear',
                description: 'Clear date button text',
              })}
              onClick={() => {
                this.setState({
                  formData: { ...this.state.formData, [item.id]: null },
                });
              }}
            />
          </Layout>
        ) : (
          <div className={classes.fieldContainer}>
            <EcDatepicker
              date={dateField ? parseDate(dateField) : null}
              isInModal={true}
              onDateChange={(date) => {
                this.setState({
                  formData: {
                    ...this.state.formData,
                    [item.id]: date ? formatDate(date, 'iso') : null,
                  },
                });
              }}
              onCalendarOpenClose={this.onCalendarOpenClose}
              isClearable={true}
              placeholder={intl.formatMessage({
                id: 'UploadModal.selectDatePlaceholder',
                defaultMessage: 'Select date',
                description: 'Placeholder text for date picker.',
              })}
            />
          </div>
        )}
      </>
    );
  }

  renderNumberItem(item) {
    const { classes, intl } = this.props;
    const field = this.state.formData[item.id];

    return (
      <div className={classes.fieldContainer}>
        <EcInput
          input={{
            value: field || '',
            onChange: (event) =>
              this.handleChangeFormField(item.id, event.target.value),
          }}
          placeholder={intl.formatMessage(
            {
              id: 'UploadModal.enterFieldPlaceholder2',
              defaultMessage: 'Enter {label}',
              description:
                'Placeholder for input fields. Example: label = "Contract Value" or "Duration"',
            },
            { label: item.label },
          )}
          type="number"
          width="100%"
        />
      </div>
    );
  }

  renderStringItem(item) {
    const { classes, intl } = this.props;
    const field = this.state.formData[item.id];

    return (
      <div className={classes.fieldContainer}>
        <EcInput
          input={{
            value: field || '',
            onChange: (event) =>
              this.handleChangeFormField(item.id, event.target.value),
          }}
          placeholder={intl.formatMessage(
            {
              id: 'UploadModal.enterFieldPlaceholder',
              defaultMessage: 'Enter {label}',
              description:
                'Placeholder for input fields. Example: label = "Document Title"',
            },
            { label: item.label },
          )}
          width="100%"
        />
      </div>
    );
  }

  renderTextAreaItem(item) {
    const { classes } = this.props;
    const presentValue = this.state.formData[item.id] || '';

    return (
      <div className={classes.fieldContainer}>
        <TextArea
          value={presentValue}
          rows="5"
          className={classes.textArea}
          onChange={(updatedValue) =>
            this.handleChangeFormField(item.id, updatedValue)
          }
        />
      </div>
    );
  }

  renderSingleArrayItem(item) {
    const { classes } = this.props;

    return (
      <div className={classes.fieldContainer}>
        <FieldValuesSingleSelect
          onChange={(newValue) => {
            const changeValue = newValue ? newValue.value : null;
            this.handleChangeFormField(item.id, changeValue);
          }}
          isClearable={true}
          isCreatable={item.allow_new_options}
          fieldId={item.id}
          fieldValues={item.values}
        />
      </div>
    );
  }

  renderMultipleArrayItem(item) {
    const { classes, intl } = this.props;
    const selectedValues = this.state.formData[item.id] || [];

    return (
      <div className={classes.editItemContainerMultiple}>
        <FieldValuesMultiSelect
          value={selectedValues}
          onChange={(newItems) => {
            this.handleChangeFormField(item.id, newItems);
          }}
          width="100%"
          placeholder={intl.formatMessage(
            {
              id: 'UploadModal.searchFieldPlaceholder',
              defaultMessage: 'Search for {label}...',
              description:
                'Placeholder for search input. Example: label = "Company" or "Department"',
            },
            { label: item.label },
          )}
          noOptionsMessage={intl.formatMessage(
            {
              id: 'UploadModal.noOptionsMessage',
              defaultMessage: 'No {label} found',
              description:
                'Message when no options are found. Example: label = "companies" or "departments"',
            },
            { label: item.label },
          )}
          isCreatable={item.allow_new_options}
          fieldId={item.id}
          fieldValues={item.values}
        />
        {this.renderMultipleValues(item)}
      </div>
    );
  }

  renderMultipleValues(item) {
    const { classes, intl } = this.props;
    const selectedValues = this.state.formData[item.id];

    if (!selectedValues) return;

    return (
      <div className={classes.multipleValuesContainer}>
        <SubArrowIcon color={black5} />
        <div className={classes.multipleValues}>
          {selectedValues.length ? (
            selectedValues.map((value, index) => (
              <EcMultipleSelectValue
                label={value.label}
                key={index}
                marginBottom="8px"
                closeHandler={() => {
                  const newMultiState = [...selectedValues];
                  this.handleChangeFormField(
                    item.id,
                    newMultiState.filter(
                      (multiValue) => multiValue.value !== value.value,
                    ),
                  );
                }}
              />
            ))
          ) : (
            <span className={classes.multipleValuesEmpty}>
              {intl.formatMessage({
                id: 'UploadModal.noValuesSelected',
                defaultMessage: 'No values selected...',
                description: 'Message when no values are selected',
              })}
            </span>
          )}
        </div>
      </div>
    );
  }

  renderField(item) {
    let returnValue;
    switch (item.type) {
      case 'BOOLEAN':
        returnValue = this.renderBooleanItem(item);
        break;
      case 'DATE':
        returnValue = this.renderDateItem(item);
        break;
      case 'NUMBER':
        returnValue = this.renderNumberItem(item);
        break;
      case 'STRING':
        returnValue = this.renderStringItem(item);
        break;
      case 'TEXT_AREA':
        returnValue = this.renderTextAreaItem(item);
        break;
      case 'ARRAY_SINGLE':
        returnValue = this.renderSingleArrayItem(item);
        break;
      case 'ARRAY_MULTIPLE':
        returnValue = this.renderMultipleArrayItem(item);
        break;
      default:
        returnValue = EM_DASH;
        break;
    }

    return returnValue;
  }

  renderFormFields() {
    const { classes, intl } = this.props;
    const { uploadFormFields, loading, errorLoading, helpText } = this.state;

    if (loading && !errorLoading) {
      return (
        <div className={classes.loadingContainer}>
          <LoadingSpinner size="medium" />
        </div>
      );
    } else if (!loading && errorLoading) {
      return (
        <div className={classes.errorMessage}>
          {intl.formatMessage({
            id: 'UploadModal.formFieldsLoadError',
            defaultMessage:
              'An error occurred while loading the upload form fields.',
            description: 'Error message when form fields fail to load',
          })}
        </div>
      );
    }

    return (
      <div className={classes.documentTagsContainer}>
        {uploadFormFields
          ? uploadFormFields.map((item) => (
              <div className={classes.subsection} key={`form-field-${item.id}`}>
                <div className={classes.subsectionTitle}>
                  {item.label}
                  {helpText && helpText[item.id] ? (
                    <Icon icon="info" tooltip={helpText[item.id]} />
                  ) : undefined}
                </div>

                {this.renderField(item)}
              </div>
            ))
          : null}
      </div>
    );
  }

  renderAddedDocuments() {
    const { classes, uploadFiles, handleRemoveUploadFile, intl } = this.props;

    return uploadFiles.map((doc, index) => {
      return doc ? (
        <Fragment key={`doc-${index}`}>
          <div className={classes.documentInfo}>
            <DocumentStrokedIcon />
            <div className={classes.documentName}>{doc.name}</div>
            <div className={classes.documentSize}>
              <UploadSize size={doc.size} />
            </div>
            <button
              className={classes.closeIcon}
              title={intl.formatMessage({
                id: 'UploadModal.removeDocumentTitle',
                defaultMessage: 'Remove document',
                description: 'Tooltip for remove document button',
              })}
              onClick={() => handleRemoveUploadFile(doc.upload.uuid)}
            >
              <CloseIcon />
            </button>
          </div>
          <hr className={classes.horizontalSeparator} />
        </Fragment>
      ) : null;
    });
  }

  render() {
    const {
      classes,
      displayInfoSection = true,
      docsSectionStyles = {},
      docsSectionTitle = 'Documents',
      title,
      uploadLocationName,
      uploadFiles,
      confirmButtonText,
      hideModal,
      showSecondaryModal,
      handleAddUploadFiles,
      hasUploadPermission,
      intl,
    } = this.props;

    return (
      <EcModalCard
        id={OnboardingIdType.UploadContractFields}
        title={title}
        content={
          <DropzoneComponent
            config={{ postUrl: 'no-url' }}
            djsConfig={{
              autoProcessQueue: false,
              clickable: '#add_documents',
              dictDefaultMessage: '',
              previewTemplate: '<div class="ignore"></div>',
            }}
            eventHandlers={{
              addedfiles: async (files) => {
                const validFiles = await filterValidFiles(files);
                setTimeout(() => {
                  handleAddUploadFiles(validFiles);
                });
              },
            }}
          >
            <div className={classes.modalBodyContent}>
              {displayInfoSection ? (
                <div className={classes.infoSection}>
                  <div className={classes.sectionHeader}>
                    <div className={classes.sectionTitle}>
                      {intl.formatMessage({
                        id: 'UploadModal.locationAndMetadata',
                        defaultMessage: 'Location and Metadata',
                        description: 'Section title for location and metadata',
                      })}
                    </div>
                  </div>
                  <div className={classes.subsection}>
                    <div className={classes.subsectionTitle}>
                      {intl.formatMessage({
                        id: 'UploadModal.locationLabel',
                        defaultMessage: 'LOCATION',
                        description: 'Label for location section',
                      })}
                    </div>
                    <div className={classes.folderLocation}>
                      <div className={classes.folderContent}>
                        <FolderIcon blue />
                        <div className={classes.folderInfo}>
                          <div className={classes.folderName}>
                            {uploadLocationName}
                          </div>
                          {this.renderSelectedFolderPath()}
                        </div>
                      </div>
                      <div className={classes.folderAction}>
                        <Button
                          text={intl.formatMessage({
                            id: 'UploadModal.changeLocationButton',
                            defaultMessage: 'Change Location',
                            description: 'Button to change upload location',
                          })}
                          size="s"
                          onClick={() => showSecondaryModal(MODAL_FOLDER_TREE)}
                        />
                      </div>
                    </div>
                  </div>
                  {this.renderFormFields()}
                </div>
              ) : null}
              {displayInfoSection ? (
                <span className={classes.sectionSeparator} />
              ) : null}
              <div
                className={classes.documentsSection}
                style={docsSectionStyles}
              >
                <div className={classes.sectionHeader}>
                  <div className={classes.sectionTitle}>
                    {docsSectionTitle} ({uploadFiles.length})
                  </div>
                  <div
                    className={classes.sectionHeaderAction}
                    id={OnboardingIdType.AddDocuments}
                  >
                    <button
                      id="add_documents"
                      className={classes.addDocsButton}
                    >
                      <PlusIcon size="20" color={evisortBlue} />
                      {intl.formatMessage(
                        {
                          id: 'UploadModal.addDocuments',
                          defaultMessage: 'Add {docsSectionTitle}',
                          description:
                            'Button to add documents. Example: docsSectionTitle = "Documents" or "Files"',
                        },
                        { docsSectionTitle },
                      )}
                    </button>
                  </div>
                </div>
                <div className={classes.documentsList}>
                  <hr className={classes.horizontalSeparator} />
                  {this.renderAddedDocuments()}
                </div>
                {!hasUploadPermission && (
                  <div className={classes.modalErrorMessage}>
                    {intl.formatMessage({
                      id: 'UploadModal.permissionError',
                      defaultMessage:
                        'Please select a folder you have access to.',
                      description: 'Error message for insufficient permissions',
                    })}
                  </div>
                )}
              </div>
            </div>
          </DropzoneComponent>
        }
        footer={
          <>
            <Button
              variant="tertiary"
              text={intl.formatMessage({
                id: 'UploadModal.cancelButton',
                defaultMessage: 'Cancel',
                description: 'Cancel button text',
              })}
              onClick={hideModal}
            />
            <Button
              variant="primary"
              id={OnboardingIdType.UploadButtonModal}
              disabled={!hasUploadPermission || uploadFiles.length === 0}
              text={confirmButtonText}
              onClick={this.handleOnUploadDocumentsClick}
            />
          </>
        }
        hideModal={hideModal}
      />
    );
  }
}

export default injectSheet(styles)(
  withFlags(injectIntl(withFormatDate(UploadModal))),
);
